export const BRAND_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const BRAND_IMAGE_BUCKET = "brand-assets";

export function validateBrandImageFile(file: Pick<File, "name" | "size" | "type">, allowIcon = false) {
  if (!file.size) throw new Error("Choose a non-empty image file.");
  if (file.size > BRAND_IMAGE_MAX_BYTES) throw new Error("Images must be 10 MB or smaller.");
  const extension = file.name.split(".").pop()?.toLowerCase();
  const types: Record<string, string[]> = {
    png: ["image/png"], jpg: ["image/jpeg"], jpeg: ["image/jpeg"],
    webp: ["image/webp"], svg: ["image/svg+xml"],
    ...(allowIcon ? { ico: ["image/x-icon", "image/vnd.microsoft.icon", "image/ico"] } : {}),
  };
  if (!extension || !types[extension] || (file.type && !types[extension].includes(file.type))) {
    throw new Error(allowIcon ? "Choose a PNG, JPEG, WebP, SVG or ICO file." : "Choose a PNG, JPEG, WebP or SVG file.");
  }
  return { extension, contentType: types[extension][0] };
}

export function validateBrandSvgText(text: string) {
  if (/<!DOCTYPE|<!ENTITY|<\?xml-stylesheet/i.test(text)) throw new Error("SVG declarations and external stylesheets are not supported.");
}

export function makeBrandImagePath(brandId: string, extension: string, uniqueId: string) {
  if (!/^[a-zA-Z0-9-]+$/.test(brandId) || !/^[a-zA-Z0-9-]+$/.test(uniqueId) || !/^(png|jpe?g|webp|svg|ico)$/.test(extension)) {
    throw new Error("Invalid image upload destination.");
  }
  return `${brandId}/${uniqueId}.${extension}`;
}

/** Strict SVG subset: no executable content, embedded documents or external resources. */
function validateSvgDocument(text: string) {
  validateBrandSvgText(text);
  const document = new DOMParser().parseFromString(text, "image/svg+xml");
  const allowed = new Set(["svg", "g", "path", "rect", "circle", "ellipse", "line", "polyline", "polygon", "text", "tspan", "title", "desc", "defs", "linearGradient", "radialGradient", "stop", "clipPath", "mask", "symbol", "use"]);
  if (document.querySelector("parsererror") || document.documentElement.localName !== "svg") throw new Error("This SVG is not a valid image.");
  for (const element of Array.from(document.querySelectorAll("*"))) {
    if (element.namespaceURI !== "http://www.w3.org/2000/svg" || !allowed.has(element.localName)) {
      throw new Error("This SVG contains unsupported embedded content. Use a PNG instead.");
    }
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.localName.toLowerCase();
      const value = attribute.value.trim();
      if (name.startsWith("on") || name === "base" || name === "src" ||
        (name === "href" && !/^#[\w.-]+$/.test(value)) ||
        /javascript:|data:|https?:|@import|expression\s*\(|\\|\/\*/i.test(value) && name !== "xmlns" ||
        /url\s*\(/i.test(value) && !/^url\(\s*["']?#[\w.-]+["']?\s*\)$/.test(value)) {
        throw new Error("This SVG contains scripts or external references. Use a PNG instead.");
      }
    }
  }
}

export async function prepareBrandImage(file: File, allowIcon = false) {
  const metadata = validateBrandImageFile(file, allowIcon);
  if (metadata.extension === "svg") {
    validateSvgDocument(await file.text());
  } else {
    const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const match = (expected: number[], offset = 0) => expected.every((byte, index) => bytes[index + offset] === byte);
    const valid = metadata.extension === "png" ? match([137,80,78,71,13,10,26,10])
      : metadata.extension === "webp" ? match([82,73,70,70]) && match([87,69,66,80], 8)
      : metadata.extension === "ico" ? match([0,0,1,0]) : match([255,216,255]);
    if (!valid) throw new Error("The file contents do not match its image type.");
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    if (!image.naturalWidth || !image.naturalHeight) throw new Error("Empty image.");
  } catch {
    throw new Error("This image could not be opened. Choose another file.");
  } finally {
    URL.revokeObjectURL(url);
  }
  return metadata;
}